// When an edge login page must not start sign-in on its own. Shared by the
// web-app and editor login pages, which otherwise go straight to the identity
// provider when there is one sign-in method or a deep link picks one.
import {
  LOGGED_OUT_PARAM,
  LOGOUT_REASON_MESSAGE,
  LOGOUT_REASON_PARAM,
  isLogoutReason,
} from "./policy";

/**
 * Set when the user signs out, cleared by the next successful sign-in. A
 * sign-out lands on the login page with a marker, but a bookmark, a new tab or
 * the router's bounce do not carry one, and the IdP may still hold the
 * previous user's session.
 */
export const SIGNED_OUT_KEY = "korra-edge-signed-out";

/**
 * How many sign-outs this origin has seen. Never cleared, unlike the flag
 * above: a sign-in clears the flag, so a page holding an earlier sign-in
 * compares this count instead, or a sign-out followed by someone else's
 * sign-in would leave it looking current.
 */
export const SIGN_OUTS_KEY = "korra-edge-sign-outs";

/** The current sign-out count, to compare against a later reading. */
export function signOutCount(storage?: Pick<Storage, "getItem">): string {
  try {
    return (storage ?? window.localStorage).getItem(SIGN_OUTS_KEY) ?? "0";
  } catch {
    return "0";
  }
}

// Fixed copy per OAuth error code (RFC 6749 §4.1.2.1, OIDC Core §3.1.2.6).
// error_description is not shown: it comes from the URL, so a crafted link
// could put any text on the real login page.
const IDP_ERROR_MESSAGE: Record<string, string> = {
  access_denied: "Sign-in was cancelled or this account is not allowed in.",
  login_required: "The identity provider needs you to sign in again.",
  consent_required: "The identity provider needs your consent first.",
  interaction_required: "The identity provider needs you to sign in again.",
  account_selection_required: "Choose an account at the identity provider.",
};

export interface AutoSignInHold {
  /** What to show in the page's message slot. */
  message: string;
  /** Make the IdP ask for an account: whoever signed out may still be there. */
  forceAccountSelection: boolean;
  /**
   * The URL itself holds the page (an IdP error, a sign-out marker), not only
   * a sign-out remembered from an earlier visit. A remembered one says nothing
   * about a session saved since, possibly by the other app on this origin.
   */
  fromUrl: boolean;
}

/**
 * Why the page must wait for a click instead of starting sign-in, or undefined
 * to go ahead:
 * - the IdP came back with an error (?error=): starting again would loop
 *   between the IdP and this page;
 * - the user signed out (?reason=, ?loggedOut=, or a sign-out remembered from
 *   before): an external IdP that still has its own session would sign the
 *   previous user straight back in. A marked arrival is remembered here.
 */
export function autoSignInHold(
  params: URLSearchParams,
  storage?: Pick<Storage, "getItem" | "setItem">,
): AutoSignInHold | undefined {
  let remembered = false;
  try {
    remembered = !!(storage ?? window.localStorage).getItem(SIGNED_OUT_KEY);
  } catch {
    /* storage blocked: only the URL can hold this visit */
  }
  const idpError = params.get("error");
  if (idpError) {
    const known = IDP_ERROR_MESSAGE[idpError];
    return {
      message: known
        ? `Sign-in did not complete. ${known}`
        : "Sign-in did not complete.",
      // An error after a sign-out (the next person cancelled the account
      // chooser) must still make the IdP ask on the next click.
      forceAccountSelection: remembered,
      fromUrl: true,
    };
  }
  const reason = params.get(LOGOUT_REASON_PARAM);
  const marked =
    (reason && isLogoutReason(reason)) || params.has(LOGGED_OUT_PARAM);
  if (marked) {
    noteSignedOut(storage);
    return {
      message:
        reason && isLogoutReason(reason)
          ? LOGOUT_REASON_MESSAGE[reason]
          : LOGOUT_REASON_MESSAGE.manual,
      forceAccountSelection: true,
      fromUrl: true,
    };
  }
  return remembered
    ? {
        message: "Choose an account to continue.",
        forceAccountSelection: true,
        fromUrl: false,
      }
    : undefined;
}

/** Remember a sign-out until the next successful sign-in. */
export function noteSignedOut(
  storage?: Pick<Storage, "getItem" | "setItem">,
): void {
  try {
    const store = storage ?? window.localStorage;
    store.setItem(SIGNED_OUT_KEY, "1");
    store.setItem(SIGN_OUTS_KEY, String(Number(signOutCount(store)) + 1));
  } catch {
    /* private mode: the URL marker still holds this visit */
  }
}

/** A sign-in completed: the next visit may start sign-in on its own again. */
export function clearSignedOut(storage?: Pick<Storage, "removeItem">): void {
  try {
    (storage ?? window.localStorage).removeItem(SIGNED_OUT_KEY);
  } catch {
    /* ignore */
  }
}

/** The OAuth callback params a spent or failed sign-in leaves in the URL. */
export const IDP_CALLBACK_PARAMS = [
  "code",
  "state",
  "error",
  "error_description",
  "error_uri",
];
